import { useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { ContractInfo, getEndRentDateString } from 'entities/Contract';
import { useReserveBoxMutation } from 'entities/Reservation';
import { getServerDateFormat } from 'shared/utils/helpers/getDateFormat';

interface HookArgs {
  userId: string | undefined;
  warehouseId: string | undefined;
  boxId: string | undefined;
  contractOptions: ContractInfo;
  onSuccess?: () => void;
}

interface HookApi {
  isOpenedConfirm: boolean;
  isLoading: boolean;
  handleReserveBox: () => void;
  openConfirmModal: () => void;
  closeConfirmModal: () => void;
}

export const useReserveBox = ({ contractOptions, boxId, userId, warehouseId, onSuccess }: HookArgs): HookApi => {
  const [isOpenedConfirm, setOpenedConfirm] = useState(false);

  const { t } = useAppTranslation('common');

  const [reserveBox, { isLoading }] = useReserveBoxMutation();

  const openConfirmModal = useCallback((): void => {
    setOpenedConfirm(true);
  }, []);

  const closeConfirmModal = useCallback((): void => {
    setOpenedConfirm(false);
  }, []);

  const handleReserveBox = useCallback(async (): Promise<void> => {
    try {
      if (userId && warehouseId && boxId && contractOptions.startDate && contractOptions.rentOption) {
        const {
          startDate,
          endDate,
          invoiceFrequencyType,
          contractDuration,
          payForEntirePeriod,
          insuranceId,
          rentOption,
          promoCodeValue,
          additionalDiscount,
        } = contractOptions;

        const endRentDate = getEndRentDateString({ startDate, endDate, contractDuration, invoiceFrequencyType });

        await reserveBox({
          boxId,
          params: {
            userId,
            warehouseId,
            invoiceFrequencyType,
            invoiceFrequency: 1,
            paymentFrequencyPeriod: payForEntirePeriod ? 'entire period' : 'regular',
            startRentDate: getServerDateFormat(startDate),
            endRentDate: getServerDateFormat(endRentDate),
            insuranceId: insuranceId || undefined,
            rentOptionId: rentOption.rentOptionId,
            promoCode: promoCodeValue,
            additionalDiscountId: additionalDiscount?.discountId,
          },
        }).unwrap();

        showNotification('info', t('Success'), t('Box has been successfully reserved'));

        onSuccess?.();
      }
    } catch (error: CustomAny) {
      console.log(error);
      showNotification('error', t('Error'), t('Error when reserving box'));
    } finally {
      closeConfirmModal();
    }
  }, [userId, warehouseId, boxId, contractOptions, reserveBox, t, onSuccess, closeConfirmModal]);

  return {
    openConfirmModal,
    closeConfirmModal,
    handleReserveBox,
    isOpenedConfirm,
    isLoading,
  };
};
